import { useCallback, useEffect, useRef, useState } from 'react';
import type { ProposalResponse } from '@shared/api';
import type { Mutation } from '@shared/types';
import { getProposal, postMutations, revert as revertApi, undo as undoApi } from '../api/client';

/**
 * One proposal, fetched once and refetched whenever the server tells us the
 * version moved (chat `state`/`done` events, mutation responses).
 *
 * There are no optimistic updates: every bound value on screen renders from
 * this single object, so one refetch updates the whole workspace coherently.
 */
export interface ProposalStore {
  response: ProposalResponse | null;
  loading: boolean;
  error: string | null;
  /** Set while a mutation POST is in flight. */
  saving: boolean;
  saveError: string | null;
  clearSaveError: () => void;
  refetch: () => Promise<void>;
  /** Refetch only if the reported version differs from what we hold. */
  syncVersion: (version: number) => void;
  mutate: (mutations: Mutation[], changeSummary: string) => Promise<void>;
  /**
   * Same wire call as `mutate`, but for inline controls (V11 budget-chip
   * popover) that own their own busy/error UI instead of the shared
   * `saving`/`saveError` state — it returns the response and rethrows on
   * failure rather than swallowing it into `saveError`. Still updates the
   * one shared response on success, so every derived view (chip, header
   * total, cards) re-renders from the same place.
   */
  applyMutations: (mutations: Mutation[], changeSummary: string) => Promise<ProposalResponse>;
  undo: () => Promise<void>;
  revert: (version: number) => Promise<void>;
}

export function useProposal(id: string | null): ProposalStore {
  const [response, setResponse] = useState<ProposalResponse | null>(null);
  const [loading, setLoading] = useState(id !== null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Kept in a ref so syncVersion doesn't need to be re-created on every render.
  const versionRef = useRef<number | null>(null);
  versionRef.current = response?.proposal.version ?? null;

  const refetch = useCallback(async () => {
    if (!id) return;
    try {
      const next = await getProposal(id);
      setResponse(next);
      setError(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (!id) {
      setResponse(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    let live = true;
    void (async () => {
      try {
        const next = await getProposal(id);
        if (!live) return;
        setResponse(next);
        setError(null);
      } catch (cause) {
        if (!live) return;
        setError(cause instanceof Error ? cause.message : String(cause));
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => {
      live = false;
    };
  }, [id]);

  const syncVersion = useCallback(
    (version: number) => {
      if (versionRef.current === version) return;
      void refetch();
    },
    [refetch],
  );

  const runWrite = useCallback(
    async (work: () => Promise<ProposalResponse>) => {
      setSaving(true);
      setSaveError(null);
      try {
        const next = await work();
        setResponse(next);
      } catch (cause) {
        setSaveError(cause instanceof Error ? cause.message : String(cause));
      } finally {
        setSaving(false);
      }
    },
    [],
  );

  const mutate = useCallback(
    async (mutations: Mutation[], changeSummary: string) => {
      if (!id) return;
      await runWrite(() => postMutations(id, mutations, changeSummary));
    },
    [id, runWrite],
  );

  const applyMutations = useCallback(
    async (mutations: Mutation[], changeSummary: string): Promise<ProposalResponse> => {
      if (!id) throw new Error('No proposal is open.');
      const next = await postMutations(id, mutations, changeSummary);
      setResponse(next);
      return next;
    },
    [id],
  );

  const undo = useCallback(async () => {
    if (!id) return;
    await runWrite(() => undoApi(id));
  }, [id, runWrite]);

  const revert = useCallback(
    async (version: number) => {
      if (!id) return;
      await runWrite(() => revertApi(id, version));
    },
    [id, runWrite],
  );

  const clearSaveError = useCallback(() => setSaveError(null), []);

  return {
    response,
    loading,
    error,
    saving,
    saveError,
    clearSaveError,
    refetch,
    syncVersion,
    mutate,
    applyMutations,
    undo,
    revert,
  };
}
